/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// State
import {
	TQueryParams,
	TSortStatus,
} from '@global-types/index';
import { TStore } from '@store/index';
import { StateCreator } from 'zustand';
import { TApiToken } from '../types';

/* -------------------------------------------------------------------------- */
/*                                  Interface                                 */
/* -------------------------------------------------------------------------- */
export interface IApiTokenSlice {
	apiTokens: TApiToken[];
	apiTokensTotal: number;
	apiTokensPage: number;
	apiTokensPageSize: number;
	apiTokensTotalPages: number;
	apiTokensSelectedRecords: TApiToken[];
	apiTokensQueryParams: TQueryParams;
	apiTokensSelectedRow: Partial<TApiToken>;
	apiTokensSortParams: string;
	apiTokenOrganization: string | null;
	apiTokensSortStatus: {};
	apiTokensImportErrors: string | null;
	apiTokensLastAccessTokenCreated: string | null;
	apiTokensOpenConfirmationModalDelete: {
		open: boolean;
		params: string;
	};
	apiTokensOpenConfirmationModalVerify: {
		open: boolean;
		params: string;
	};
	apiTokensOpenConfirmationModalMint: {
		open: boolean;
		params: string;
	};

	setApiTokensLastAccessTokenCreated: (
		_apiTokensLastAccessTokenCreated: string
	) => void;

	setApiTokensImportErrors: (_errors: string) => void;

	setApiTokenOrganization: (
		_organizationId: string | null
	) => void;

	setApiTokensPage: (_apiTokensPage: number) => void;

	setApiTokensSelectedRecords: (
		_apiTokensSelectedRecords: TApiToken[]
	) => void;

	setApiTokensOpenConfirmationModalDelete: (_apiTokensOpenConfirmationModalDelete: {
		open: boolean;
		params: any;
	}) => void;

	setApiTokensOpenConfirmationModalVerify: (_apiTokensOpenConfirmationModalVerify: {
		open: boolean;
		params: any;
	}) => void;
	setApiTokensOpenConfirmationModalMint: (_apiTokensOpenConfirmationModalMint: {
		open: boolean;
		params: any;
	}) => void;

	setApiTokensQueryParams: (
		_apiTokensQueryParams: TQueryParams
	) => void;

	setApiTokensSortStatus: (
		_apiTokensSortStatus: TSortStatus
	) => void;
	setApiTokensSortParams: (
		_apiTokensSortParams: string
	) => void;

	setApiTokensSelectedRow: (_apiToken: TApiToken) => void;
	setApiTokensPageSize: (_pageSize: number) => void;
}

/* -------------------------------------------------------------------------- */
/*                                    Slice                                   */
/* -------------------------------------------------------------------------- */

const initialState = {
	apiTokens: [],
	apiTokensTotal: 0,
	apiTokensPage: 1,
	apiTokensPageSize: 100,
	apiTokensTotalPages: 0,
	apiTokensSelectedRecords: [],
	apiTokensQueryParams: {},
	apiTokensSelectedRow: {},
	apiTokensSortParams: '-createdAt',
	apiTokenOrganization: null,
	apiTokensSortStatus: {},
	apiTokensImportErrors: null,
	apiTokensLastAccessTokenCreated: null,
	apiTokensOpenConfirmationModalDelete: {
		open: false,
		params: '',
	},
	apiTokensOpenConfirmationModalVerify: {
		open: false,
		params: '',
	},
	apiTokensOpenConfirmationModalMint: {
		open: false,
		params: '',
	},
};

export const createApiTokensSlice: StateCreator<
	TStore,
	[],
	[],
	IApiTokenSlice
> = (set) => ({
	...initialState,
	setApiTokenOrganization: (
		_organizationId: string | null
	) => set({ apiTokenOrganization: _organizationId }),

	setApiTokensLastAccessTokenCreated: (
		_apiTokensLastAccessTokenCreated: string
	) =>
		set({
			apiTokensLastAccessTokenCreated:
				_apiTokensLastAccessTokenCreated,
		}),

	setApiTokensImportErrors: (_errors: string) =>
		set({ apiTokensImportErrors: _errors }),

	setApiTokensSelectedRow: (_apiToken: TApiToken) =>
		set({ apiTokensSelectedRow: _apiToken }),

	setApiTokensSortParams: (_sortStatus: string) =>
		set({ apiTokensSortParams: _sortStatus }),

	setApiTokensSortStatus: (_sortStatus: TSortStatus) =>
		set({ apiTokensSortStatus: _sortStatus }),

	setApiTokensQueryParams: (_queryParams: TQueryParams) =>
		set({ apiTokensQueryParams: _queryParams }),
	setApiTokensTotal: (_total: number) =>
		set({ apiTokensTotal: _total }),
	setApiTokensOpenConfirmationModalDelete:
		(_apiTokensOpenConfirmationModalDelete: {
			open: boolean;
			params: any;
		}) =>
			set({
				apiTokensOpenConfirmationModalDelete:
					_apiTokensOpenConfirmationModalDelete,
			}),
	setApiTokensOpenConfirmationModalMint:
		(_apiTokensOpenConfirmationModalMint: {
			open: boolean;
			params: string;
		}) =>
			set({
				apiTokensOpenConfirmationModalMint:
					_apiTokensOpenConfirmationModalMint,
			}),
	setApiTokensOpenConfirmationModalVerify:
		(_apiTokensOpenConfirmationModalVerify: {
			open: boolean;
			params: string;
		}) =>
			set({
				apiTokensOpenConfirmationModalVerify:
					_apiTokensOpenConfirmationModalVerify,
			}),
	setApiTokensPage: (_page: number) =>
		set({ apiTokensPage: _page }),
	setApiTokensPageSize: (_pageSize: number) =>
		set({ apiTokensPageSize: _pageSize }),
	setApiTokensTotalPages: (_totalPages: number) =>
		set({ apiTokensTotalPages: _totalPages }),
	setApiTokensSelectedRecords: (
		_apiTokensSelectedRecords: TApiToken[]
	) =>
		set({
			apiTokensSelectedRecords: _apiTokensSelectedRecords,
		}),
});
